import { useContext, useEffect, useState } from 'react';
import tableCellRenderTypes from '../../viewDashboards/widgets/customizableTable/tableCells/tableCellRenderTypes';
import {
    buildLinkConfiguration,
    getDashboardLinkParameterForDashboard,
} from '../../viewDashboards/dashboardLinkUtils';
import DashboardContext from './dashboardContext';
import { useLabelContext } from './labelContext';

export function getSystemOrigin(linkConfig, systemManifest) {
    if (!systemManifest) {
        return window.location.origin;
    }

    const systemOrigin =
        systemManifest[linkConfig.destinationSystemKey]?.systemOrigin;

    return systemOrigin ?? window.location.origin;
}

function getLink(
    linkConfig,
    getNonApiUri,
    externalLinkParameter,
    systemManifest
) {
    if (linkConfig.isAbsoluteUrl) {
        return linkConfig.pathName;
    }

    const systemOrigin = getSystemOrigin(linkConfig, systemManifest);
    const pathname = getNonApiUri(linkConfig.pathName);

    return `${systemOrigin}${pathname}?${externalLinkParameter}`;
}

async function getAdvancedSearchLinkUri(
    buildAdvancedSearchExternalLinkParameter,
    metricRequest,
    dataRow,
    linkConfig,
    dashboardLabels,
    getNonApiUri,
    setUri,
    systemManifest
) {
    if (!buildAdvancedSearchExternalLinkParameter) {
        throw Error(
            'Missing customization function buildAdvancedSearchExternalLinkParameter'
        );
    }
    const externalLinkParameter =
        await buildAdvancedSearchExternalLinkParameter(
            metricRequest.timeAndPrecision,
            metricRequest.filters,
            metricRequest.where,
            dataRow,
            linkConfig.argumentConfigs,
            dashboardLabels
        );

    setUri(
        getLink(linkConfig, getNonApiUri, externalLinkParameter, systemManifest)
    );
}

async function getDashboardLinkUri(
    metricRequest,
    dataRow,
    linkConfig,
    dashboardLabels,
    getNonApiUri,
    setUri,
    systemManifest
) {
    const linkConfiguration = buildLinkConfiguration(
        linkConfig.argumentConfigs,
        metricRequest.filters,
        metricRequest.timeAndPrecision,
        metricRequest.where,
        dataRow,
        dashboardLabels
    );
    const externalLinkParameter = await getDashboardLinkParameterForDashboard(
        linkConfiguration
    );

    setUri(
        getLink(linkConfig, getNonApiUri, externalLinkParameter, systemManifest)
    );
}

export function buildLinkWithParametersUri(
    dataRow,
    linkConfig,
    getNonApiUri,
    systemManifest
) {
    if (linkConfig.isAbsoluteUrl) {
        return linkConfig.pathName;
    }

    const systemOrigin = getSystemOrigin(linkConfig, systemManifest);
    const pathname = getNonApiUri(linkConfig.pathName);
    const parameters = linkConfig.parameters.map((x) => `${dataRow[x]}/`);

    return `${systemOrigin}${pathname}/${parameters}`;
}

function getLinkWithParametersUri(
    dataRow,
    linkConfig,
    getNonApiUri,
    setUri,
    systemManifest
) {
    setUri(
        buildLinkWithParametersUri(
            dataRow,
            linkConfig,
            getNonApiUri,
            systemManifest
        )
    );
}

export default function useLink(linkConfig, metricRequest, dataRow) {
    const { dashboardLabels, predicateLabels } = useLabelContext();
    const { customizations, uriConstants, systemManifest } =
        useContext(DashboardContext);
    const [uri, setUri] = useState('');

    useEffect(() => {
        switch (linkConfig?.renderType) {
            case tableCellRenderTypes.advancedSearchLink:
                getAdvancedSearchLinkUri(
                    customizations?.buildAdvancedSearchExternalLinkParameter,
                    metricRequest,
                    dataRow,
                    linkConfig,
                    dashboardLabels,
                    uriConstants.getNonApiUri,
                    setUri,
                    systemManifest
                );
                break;
            case tableCellRenderTypes.dashboardLink:
                getDashboardLinkUri(
                    metricRequest,
                    dataRow,
                    linkConfig,
                    predicateLabels,
                    uriConstants.getNonApiUri,
                    setUri,
                    systemManifest
                );
                break;
            case tableCellRenderTypes.linkWithParameters:
                getLinkWithParametersUri(
                    dataRow,
                    linkConfig,
                    uriConstants.getNonApiUri,
                    setUri,
                    systemManifest
                );
                break;
            default:
                setUri(linkConfig?.pathName ?? '');
                break;
        }
    }, [
        customizations,
        dashboardLabels,
        dataRow,
        linkConfig,
        metricRequest,
        predicateLabels,
        uriConstants,
        systemManifest,
    ]);

    return uri;
}
